import React, { useEffect, useState } from 'react';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import MapTouchGate from './map/MapTouchGate';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useOrgTimezone, formatDateTimeInTz } from '@/utils/timezone';
import { getToken } from '@/services/api';

// Fix for default marker icons in React-Leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
import type { NumericValue } from '@/utils/numeric';
import { formatNum, toNumOr } from '@/utils/numeric';

delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
    iconUrl: markerIcon,
    iconRetinaUrl: markerIcon2x,
    shadowUrl: markerShadow,
});

interface SensorReading {
    temperature: NumericValue;
    humidity: NumericValue;
    pH: NumericValue;
    nitrogen: NumericValue;
    phosphorus: NumericValue;
    potassium: NumericValue;
    conductivity: NumericValue;
    timestamp: number;
}

interface SensorLocation {
    deviceId: string;
    latitude: NumericValue;
    longitude: NumericValue;
    lastReading: SensorReading;
}

interface SensorMapProps {
    onDeviceClick?: (deviceId: string) => void;
}

const getAuthHeaders = () => {
    const token = getToken();
    return {
        'Content-Type': 'application/json',
        ...(token ? { 'Authorization': `Bearer ${token}` } : {})
    };
};

const SensorMap: React.FC<SensorMapProps> = ({ onDeviceClick }) => {
    const orgTz = useOrgTimezone();
    const [sensors, setSensors] = useState<SensorLocation[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        fetchSensors();
    }, []);

    const fetchSensors = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${window.location.origin}/api/sensors/sensors-map`, {
                headers: getAuthHeaders()
            });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}`);
            }

            const data = await response.json();
            setSensors(data.sensors || []);
            setError(null);
        } catch (err) {
            console.error('Error fetching sensor locations:', err);
            setError('Failed to load sensor locations');
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="text-center">
                    <div className="text-4xl mb-2">⏳</div>
                    <p className="text-neutral-600">Loading map...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="text-center">
                    <div className="text-4xl mb-2">⚠</div>
                    <p className="text-red-600">{error}</p>
                    <button
                        onClick={fetchSensors}
                        className="mt-4 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    if (sensors.length === 0) {
        return (
            <div className="flex items-center justify-center h-full min-h-[400px]">
                <div className="text-center">
                    <div className="text-6xl mb-4">📍</div>
                    <h3 className="text-xl font-semibold text-neutral-700 mb-2">No Sensors with GPS Data</h3>
                    <p className="text-neutral-500">No sensors with GPS coordinates found for your organization.</p>
                </div>
            </div>
        );
    }

    // Calculate the center of all sensors
    const centerLat = sensors.reduce((sum, s) => sum + toNumOr(s.latitude), 0) / sensors.length;
    const centerLng = sensors.reduce((sum, s) => sum + toNumOr(s.longitude), 0) / sensors.length;

    // Calculate appropriate zoom level based on sensor spread
    const latitudes = sensors.map(s => toNumOr(s.latitude));
    const longitudes = sensors.map(s => toNumOr(s.longitude));
    const latSpread = Math.max(...latitudes) - Math.min(...latitudes);
    const lngSpread = Math.max(...longitudes) - Math.min(...longitudes);
    const maxSpread = Math.max(latSpread, lngSpread);

    let zoom = 13;
    if (maxSpread > 1) zoom = 8;
    else if (maxSpread > 0.5) zoom = 10;
    else if (maxSpread > 0.1) zoom = 12;

    return (
        <div className="h-full w-full" style={{ minHeight: '500px' }}>
            <MapContainer
                center={[centerLat, centerLng]}
                zoom={zoom}
                style={{ height: '100%', width: '100%', minHeight: '500px' }}
                className="rounded-lg"
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {sensors.map((sensor) => (
                    <Marker
                        key={sensor.deviceId}
                        position={[toNumOr(sensor.latitude), toNumOr(sensor.longitude)]}
                        eventHandlers={{
                            click: () => {
                                if (onDeviceClick) {
                                    onDeviceClick(sensor.deviceId);
                                }
                            }
                        }}
                    >
                        <Popup>
                            <div className="min-w-[250px]">
                                <h3 className="font-bold text-lg mb-2 text-neutral-800">
                                    Device {sensor.deviceId}
                                </h3>
                                <div className="text-sm space-y-1">
                                    <p><strong>Temperature:</strong> {formatNum(sensor.lastReading.temperature, 1)}°C</p>
                                    <p><strong>Humidity:</strong> {formatNum(sensor.lastReading.humidity, 1)}%</p>
                                    <p><strong>pH:</strong> {formatNum(sensor.lastReading.pH, 2)}</p>
                                    <p><strong>Conductivity:</strong> {formatNum(sensor.lastReading.conductivity, 0)} µS/cm</p>
                                    <p><strong>N-P-K:</strong> {formatNum(sensor.lastReading.nitrogen, 0)}-{formatNum(sensor.lastReading.phosphorus, 0)}-{formatNum(sensor.lastReading.potassium, 0)} mg/kg</p>
                                    <p className="text-xs text-neutral-500 mt-2">
                                        Last update: {formatDateTimeInTz(new Date(sensor.lastReading.timestamp * 1000).toISOString(), orgTz)}
                                    </p>
                                </div>
                                {onDeviceClick && (
                                    <button
                                        onClick={() => onDeviceClick(sensor.deviceId)}
                                        className="mt-3 w-full px-3 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 text-sm font-medium"
                                    >
                                        View Details
                                    </button>
                                )}
                            </div>
                        </Popup>
                    </Marker>
                ))}
              <MapTouchGate />
            </MapContainer>
        </div>
    );
};

export default SensorMap;
